import { useState } from 'react';
import type { ReactElement } from 'react';
import type { LeaderboardPeriod } from '@ponzinomics/types';
import { Button, toast } from '@ponzinomics/ui';
import { useLeaderboard } from '../../hooks/use-leaderboard';
import { useLeaderboardConfigs, type LeaderboardConfig } from '../../hooks/use-leaderboard-configs';

const DEMO_CONFIGS: LeaderboardConfig[] = [
  {
    id: 'demo-points',
    projectId: 'demo',
    name: 'Points Leaders',
    metricField: 'points',
    sortOrder: 'desc',
    displayLimit: 100,
    timeWindow: 'all-time',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-quests',
    projectId: 'demo',
    name: 'Quest Champions',
    metricField: 'questsCompleted',
    sortOrder: 'desc',
    displayLimit: 50,
    timeWindow: 'weekly',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'demo-referrals',
    projectId: 'demo',
    name: 'Top Referrers',
    metricField: 'referrals',
    sortOrder: 'desc',
    displayLimit: 25,
    timeWindow: 'monthly',
    isActive: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

const DEMO_ENTRIES = [
  { rank: 1, displayName: 'CryptoWhale.eth', points: 48250 },
  { rank: 2, displayName: 'DeFiKing_42', points: 41800 },
  { rank: 3, displayName: '0xAlpha', points: 38920 },
  { rank: 4, displayName: 'MoonShotMike', points: 35100 },
  { rank: 5, displayName: 'TokenMaster', points: 31450 },
  { rank: 6, displayName: 'NFTCollector', points: 28700 },
  { rank: 7, displayName: 'ChainRunner', points: 25300 },
  { rank: 8, displayName: 'YieldFarmer99', points: 22150 },
  { rank: 9, displayName: 'SatoshiFan', points: 19800 },
  { rank: 10, displayName: 'EtherVault', points: 17500 },
];

export function LeaderboardsPage(): ReactElement {
  const {
    configs: leaderboards,
    isLoading: isLoadingConfigs,
    createConfig,
    updateConfig,
    deleteConfig,
  } = useLeaderboardConfigs();
  const [showEditor, setShowEditor] = useState(false);
  const [editingBoard, setEditingBoard] = useState<LeaderboardConfig | null>(null);
  const [showPodium, setShowPodium] = useState(true);

  const displayConfigs = leaderboards.length > 0 ? leaderboards : DEMO_CONFIGS;

  const [viewingConfig, setViewingConfig] = useState<LeaderboardConfig | null>(
    displayConfigs[0] ?? null
  );

  const activePeriod: LeaderboardPeriod = (viewingConfig?.timeWindow ??
    'all-time') as LeaderboardPeriod;

  const { data: leaderboardData, isLoading: isLoadingPreview } = useLeaderboard(activePeriod);
  const rawEntries = leaderboardData?.entries ?? [];
  const previewEntries = rawEntries.length > 0 ? rawEntries : DEMO_ENTRIES;

  const handleSave = async (board: LeaderboardConfig): Promise<void> => {
    if (editingBoard) {
      await updateConfig({
        configId: board.id,
        dto: {
          name: board.name,
          metricField: board.metricField,
          sortOrder: board.sortOrder,
          displayLimit: board.displayLimit,
          timeWindow: board.timeWindow,
          isActive: board.isActive,
        },
      });
    } else {
      await createConfig({
        name: board.name,
        metricField: board.metricField,
        sortOrder: board.sortOrder,
        displayLimit: board.displayLimit,
        timeWindow: board.timeWindow,
        isActive: board.isActive,
      });
    }
    setShowEditor(false);
    setEditingBoard(null);
  };

  const handleEdit = (board: LeaderboardConfig): void => {
    setEditingBoard(board);
    setShowEditor(true);
  };

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await deleteConfig(id);
      if (viewingConfig?.id === id) setViewingConfig(null);
    } catch {
      toast({ title: 'Failed to delete leaderboard', variant: 'destructive' });
    }
  };

  const handleToggle = async (id: string): Promise<void> => {
    const board = leaderboards.find((b) => b.id === id);
    if (board) {
      try {
        await updateConfig({ configId: id, dto: { isActive: !board.isActive } });
      } catch {
        toast({ title: 'Failed to update leaderboard', variant: 'destructive' });
      }
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-2xl font-semibold text-white">Leaderboards</h1>
        <p className="mt-1 text-sm text-gray-500">Configure rankings for your community</p>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-12">
        {/* Left: Configurations */}
        <div className="max-w-md flex-1">
          <div className="mb-4 flex items-center justify-between">
            <label className="text-sm font-medium text-white">Configurations</label>
            <Button onClick={() => setShowEditor(true)} size="sm" variant="ghost">
              <PlusIcon className="mr-1 h-3.5 w-3.5" />
              Add
            </Button>
          </div>

          <div className="space-y-2">
            {isLoadingConfigs
              ? Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg border border-white/[0.06] px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-6 w-11 animate-pulse rounded-full bg-white/[0.06]" />
                      <div>
                        <div className="mb-1 h-4 w-28 animate-pulse rounded bg-white/[0.06]" />
                        <div className="h-3 w-20 animate-pulse rounded bg-white/[0.06]" />
                      </div>
                    </div>
                  </div>
                ))
              : displayConfigs.map((board) => (
                  <div
                    key={board.id}
                    className={`flex items-center justify-between rounded-lg border px-4 py-3 transition-colors ${
                      viewingConfig?.id === board.id
                        ? 'border-brand-400/30 bg-brand-400/5'
                        : 'border-white/[0.06] hover:border-white/[0.12]'
                    } ${!board.isActive ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <Toggle enabled={board.isActive} onChange={() => handleToggle(board.id)} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{board.name}</span>
                        </div>
                        <p className="text-xs text-gray-600">
                          {board.timeWindow === 'all-time' ? 'All time' : board.timeWindow} · Top{' '}
                          {board.displayLimit}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setViewingConfig(viewingConfig?.id === board.id ? null : board)
                        }
                        title="View entries"
                        className={`rounded p-1.5 transition-colors ${
                          viewingConfig?.id === board.id
                            ? 'bg-brand-400/10 text-brand-400'
                            : 'text-gray-500 hover:bg-white/[0.05] hover:text-white'
                        }`}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEdit(board)}
                        className="rounded p-1.5 text-gray-500 hover:bg-white/[0.05] hover:text-white"
                      >
                        <EditIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(board.id)}
                        className="rounded p-1.5 text-gray-500 hover:bg-white/[0.05] hover:text-red-400"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

            {!isLoadingConfigs && displayConfigs.length === 0 && (
              <p className="py-8 text-center text-sm text-gray-600">No leaderboards yet</p>
            )}
          </div>
        </div>

        {/* Right: Entries viewer or preview */}
        <div className="w-72">
          {viewingConfig ? (
            <>
              {/* Entries header */}
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-white">{viewingConfig.name}</label>
                  <p className="mt-0.5 text-xs capitalize text-gray-600">
                    {viewingConfig.timeWindow === 'all-time'
                      ? 'All time'
                      : viewingConfig.timeWindow}{' '}
                    · {viewingConfig.metricField}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setViewingConfig(null)}
                  className="rounded p-1 text-gray-500 transition-colors hover:bg-white/[0.05] hover:text-white"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="notch-border-lg notch-border-white/8 notch-bg-transparent overflow-hidden bg-gradient-to-b from-white/[0.03] to-transparent">
                {/* Stats bar */}
                <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-2.5">
                  <span className="text-xs text-gray-500">Total participants</span>
                  <span className="text-xs font-medium text-white">
                    {leaderboardData
                      ? leaderboardData.totalParticipants.toLocaleString()
                      : previewEntries.length.toLocaleString()}
                  </span>
                </div>

                {/* Ranked entries */}
                <div className="divide-y divide-white/[0.04]">
                  {isLoadingPreview ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-3 px-4 py-3">
                        <div className="h-5 w-5 animate-pulse rounded-full bg-white/[0.06]" />
                        <div className="h-5 w-5 animate-pulse rounded-full bg-white/[0.06]" />
                        <div className="h-4 w-24 animate-pulse rounded bg-white/[0.06]" />
                        <div className="ml-auto h-4 w-14 animate-pulse rounded bg-white/[0.06]" />
                      </div>
                    ))
                  ) : previewEntries.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-600">No entries yet</p>
                  ) : (
                    previewEntries.slice(0, viewingConfig.displayLimit).map((entry) => (
                      <div
                        key={entry.rank}
                        className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/[0.02]"
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            entry.rank === 1
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : entry.rank === 2
                                ? 'bg-gray-500/20 text-gray-400'
                                : entry.rank === 3
                                  ? 'bg-orange-500/20 text-orange-400'
                                  : 'text-gray-600'
                          }`}
                        >
                          {entry.rank}
                        </span>
                        <div className="h-6 w-6 shrink-0 rounded-full bg-gray-700" />
                        <span className="flex-1 truncate text-sm text-white">
                          {entry.displayName}
                        </span>
                        <span className="text-brand-400 font-mono text-xs">
                          {entry.points.toLocaleString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Preview header */}
              <div className="mb-4 flex items-center justify-between">
                <label className="text-sm font-medium text-white">Preview</label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Podium</span>
                  <Toggle enabled={showPodium} onChange={setShowPodium} />
                </div>
              </div>

              <div className="notch-border-lg notch-border-white/8 notch-bg-transparent overflow-hidden bg-gradient-to-b from-white/[0.03] to-transparent p-4">
                {/* Top 3 Podium - Animated */}
                <div
                  className={`grid transition-all duration-500 ease-out ${
                    showPodium ? 'mb-4 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="flex items-end justify-center gap-2 pb-1">
                      {/* 2nd Place */}
                      <div
                        className={`flex flex-col items-center transition-all delay-100 duration-500 ${
                          showPodium ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                      >
                        <div className="mb-1 h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 shadow-lg shadow-gray-500/20" />
                        <div className="flex h-12 w-16 items-end justify-center rounded-t-lg bg-gradient-to-t from-gray-500/30 to-gray-500/10 pb-1">
                          <span className="text-lg font-bold text-gray-400">2</span>
                        </div>
                      </div>
                      {/* 1st Place */}
                      <div
                        className={`flex flex-col items-center transition-all duration-500 ${
                          showPodium ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                      >
                        <div className="relative mb-1">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 shadow-lg shadow-yellow-500/30 ring-2 ring-yellow-400/50" />
                          <CrownIcon className="absolute -top-2.5 left-1/2 h-5 w-5 -translate-x-1/2 text-yellow-400 drop-shadow-lg" />
                        </div>
                        <div className="flex h-16 w-16 items-end justify-center rounded-t-lg bg-gradient-to-t from-yellow-500/30 to-yellow-500/10 pb-1">
                          <span className="text-xl font-bold text-yellow-400">1</span>
                        </div>
                      </div>
                      {/* 3rd Place */}
                      <div
                        className={`flex flex-col items-center transition-all delay-200 duration-500 ${
                          showPodium ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                        }`}
                      >
                        <div className="mb-1 h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-500/20" />
                        <div className="flex h-10 w-16 items-end justify-center rounded-t-lg bg-gradient-to-t from-orange-500/30 to-orange-500/10 pb-1">
                          <span className="text-lg font-bold text-orange-400">3</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Separator when podium is shown */}
                <div
                  className={`mb-3 h-px bg-white/[0.06] transition-opacity duration-300 ${
                    showPodium ? 'opacity-100' : 'opacity-0'
                  }`}
                />

                {/* Leaderboard List */}
                <div className="space-y-1">
                  {isLoadingPreview
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2">
                          <div className="h-6 w-6 animate-pulse rounded-full bg-white/[0.06]" />
                          <div className="h-6 w-6 animate-pulse rounded-full bg-white/[0.06]" />
                          <div className="h-4 w-24 animate-pulse rounded bg-white/[0.06]" />
                          <div className="ml-auto h-4 w-12 animate-pulse rounded bg-white/[0.06]" />
                        </div>
                      ))
                    : (showPodium
                        ? previewEntries.filter((e) => e.rank > 3).slice(0, 4)
                        : previewEntries.slice(0, 5)
                      ).map((entry, index) => (
                        <div
                          key={entry.rank}
                          className="flex items-center gap-3 rounded-lg px-3 py-2 transition-all duration-300 hover:bg-white/[0.03]"
                          style={{ transitionDelay: `${index * 50}ms` }}
                        >
                          <span
                            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                              entry.rank === 1
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : entry.rank === 2
                                  ? 'bg-gray-500/20 text-gray-400'
                                  : entry.rank === 3
                                    ? 'bg-orange-500/20 text-orange-400'
                                    : 'text-gray-600'
                            }`}
                          >
                            {entry.rank}
                          </span>
                          <div className="h-6 w-6 rounded-full bg-gray-700" />
                          <span className="flex-1 truncate text-sm text-white">
                            {entry.displayName}
                          </span>
                          <span className="text-brand-400 font-mono text-xs">
                            {entry.points.toLocaleString()}
                          </span>
                        </div>
                      ))}
                </div>
              </div>

              <p className="mt-2 text-center text-xs text-gray-600">
                {showPodium ? 'Top 3 highlighted with podium' : 'Standard list view'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <LeaderboardEditor
          board={editingBoard}
          onSave={handleSave}
          onClose={() => {
            setShowEditor(false);
            setEditingBoard(null);
          }}
        />
      )}
    </div>
  );
}

function LeaderboardEditor({
  board,
  onSave,
  onClose,
}: {
  board: LeaderboardConfig | null;
  onSave: (board: LeaderboardConfig) => Promise<void>;
  onClose: () => void;
}): ReactElement {
  const [name, setName] = useState(board?.name ?? '');
  const [metricField, setMetricField] = useState(board?.metricField ?? 'points');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (board?.sortOrder as 'asc' | 'desc') ?? 'desc'
  );
  const [displayLimit, setDisplayLimit] = useState(board?.displayLimit ?? 100);
  const [timeWindow, setTimeWindow] = useState(board?.timeWindow ?? 'all-time');
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    setIsSaving(true);
    try {
      await onSave({
        id: board?.id ?? '',
        projectId: board?.projectId ?? '',
        name,
        metricField,
        sortOrder,
        displayLimit,
        timeWindow,
        isActive: board?.isActive ?? true,
        createdAt: board?.createdAt ?? new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-xl border border-white/[0.08] bg-gray-950 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-white">
            {board ? 'Edit Leaderboard' : 'New Leaderboard'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">Configure ranking settings</p>
        </div>

        <div className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-white">Name</label>
            <input
              type="text"
              placeholder="Points Leaders"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="focus:border-brand-400 w-full rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-sm text-white transition-colors placeholder:text-gray-600 hover:border-white/[0.12] focus:outline-none"
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-white">Metric</label>
              <select
                value={metricField}
                onChange={(e) => setMetricField(e.target.value)}
                className="focus:border-brand-400 w-full rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-sm text-white focus:outline-none"
              >
                <option value="points">Points</option>
                <option value="questsCompleted">Quests</option>
                <option value="referrals">Referrals</option>
                <option value="volume">Volume</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-white">Sort</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                className="focus:border-brand-400 w-full rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-sm text-white focus:outline-none"
              >
                <option value="desc">Highest first</option>
                <option value="asc">Lowest first</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-white">Time Window</label>
              <select
                value={timeWindow}
                onChange={(e) => setTimeWindow(e.target.value as LeaderboardConfig['timeWindow'])}
                className="focus:border-brand-400 w-full rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-sm text-white focus:outline-none"
              >
                <option value="all-time">All time</option>
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="daily">Daily</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium text-white">Show Top</label>
              <input
                type="number"
                value={displayLimit}
                onChange={(e) => setDisplayLimit(parseInt(e.target.value) || 10)}
                min={1}
                max={1000}
                className="focus:border-brand-400 w-full rounded-lg border border-white/[0.08] bg-transparent px-3 py-2 text-sm text-white focus:outline-none"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={!name.trim() || isSaving}>
              {isSaving ? 'Saving...' : board ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Toggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-brand-400' : 'bg-gray-700'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function EyeIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

function EditIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
      />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }): ReactElement {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3h14v2H5v-2z" />
    </svg>
  );
}
